/**
 * Workspace context contains
 *
 * - Information about the workspace, synced with the API
 * - Local state, eg selected layer / tool -> only stored in the page, lost on refresh
 */

import * as React from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

import { createDataStore } from "app/lib/datastore";


const WorkspaceReactContext = React.createContext(null);
WorkspaceReactContext.displayName = "WorkspaceContext";


export const DEFAULT_LOCAL_STATE = {
    viewSettings: {
        reticle: {
            show: false,
            color: "#000",
        }
    },
};


class WorkspaceContext {
    constructor({
        queryState, // { data, loading, error, refetch, ... }
        localState,  // [ state, setState ]
        // MUTATIONS...  // [ doMutation, { data, loading, error, ... } ]
    }) {
        this._queryState = queryState;
        this._localState = localState;
    }

    // Query state from API ------------------------------------------

    get data() {
        // console.log("Query state", this._queryState);
        return this._queryState?.data?.workspace || null;
    }

    get isLoading() {
        return this._queryState?.loading;
    }

    // TODO: add something to handle 404s

    refetch() {
        const refetch = this._queryState?.refetch;
        if (refetch) {
            refetch();
        }
    }

    // Local state ---------------------------------------------------

    get localState() {
        if (!this._localState) {
            return null;
        }
        const state = this._localState[0];
        return state;
    }

    setLocalState(...args) {
        if (!this._localState) {
            return null;
        }
        const setState = this._localState[1];
        return setState(...args);
    }

    // Mutations -----------------------------------------------------
}


export function WorkspaceContextProvider({ workspaceId, children }) {

    let queryState = useQuery(QUERY_WORKSPACE, { variables: { workspaceId }});

    // Add some dummy response data to the workspace, to mock data not
    // yet available from the API.
    // FIXME: remove this before prod
    queryState = patchWorkspaceResponse(queryState);

    let localState = React.useState(DEFAULT_LOCAL_STATE);
    let context = new WorkspaceContext({ queryState, localState });
    return (
        <WorkspaceReactContext.Provider value={context}>
            {children}
        </WorkspaceReactContext.Provider>
    );
}


const QUERY_WORKSPACE = gql`
    query($workspaceId: Int!) {
        workspace(workspaceId: $workspaceId) {
            id
            ownerId
            metadata {
                displayName
            }
        }
    }
`;


export function useWorkspaceContext() {
    return React.useContext(WorkspaceReactContext);
}


const DUMMY_LAYERS = [
    { id: "layer-0", displayName: "Layer 1" },
    { id: "layer-1", displayName: "Layer 2" },
    { id: "layer-2", displayName: "Layer 3" },
    { id: "layer-3", displayName: "Layer 4" },
    { id: "layer-4", displayName: "Layer 5" },
    { id: "layer-5", displayName: "Some layer" },
    { id: "layer-6", displayName: "Some other layer" },
    { id: "layer-7", displayName: "Some points layer" },
    { id: "layer-8", displayName: "Some lines layer" },
    { id: "layer-9", displayName: "Some polygons layer" },
]


function patchWorkspaceResponse({ data, ...other }) {
    return {
        ...other,
        data: data ? {
            ...data,
            workspace: data.workspace ? {
                ...data.workspace,
                layers: DUMMY_LAYERS,
            } : data.workspace,
        } : data,
    }
}
